import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Divider, Form, Select, Space } from 'antd';
import { DeleteOutlined, NodeExpandOutlined, PlusOutlined } from '@ant-design/icons';
import { calculCondition, CONDITIONS, useFormPath } from './index';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const CONDITIONS_ACTIONS = [
    `Masquer`,
    `Afficher`
]

export default (formPlugin, namespace) =>
{

    const FormComponent = (props) => {

        const { data } = props

        const [show, setShow] = useState(true)
        const refreshForm = () => {
            setShow(false)
            setTimeout(() => setShow(true), 100)
        }

        useEffect(() => {
            refreshForm()
        }, [props.lang, props.nodeId, props.device])

        // getFormFieldsV2 (et pas getFormFields) : elle applique reellement le
        // filtre de namespace — la condition ne doit voir que les champs de sa
        // portee, pas ceux des autres produits — et dedoublonne par fieldKey.
        // Retour : [{label, value}].
        const formFields = formPlugin.getFormFieldsV2(props.pluginConfig.namespace)

        if (!show) {
            return null
        }

        return (
            <Form
                layout="vertical"
                initialValues={data}
                onValuesChange={(_, allValues) => {
                    props.onChange(allValues)
                }}
            >

                <Form.Item name={"default"} label={<Trans>Par Défaut</Trans>}>
                    <Select>
                        {CONDITIONS_ACTIONS.map(i => <Select.Option key={i} value={i}>{i}</Select.Option>)}
                    </Select>
                </Form.Item>

                <Divider><Trans>Conditions</Trans></Divider>
                <div style={{ marginBottom: 20 }}>
                    <Form.List name="conditions">
                        {(fields, { add, remove }) => (
                            <div style={{ width: 310 }}>
                                {fields.map((field, index) => {
                                    return (
                                        <Card
                                            title={<Trans>Condition {index + 1}</Trans>}
                                            type="inner"
                                            key={field.key}
                                            extra={(<DeleteOutlined onClick={() => remove(field.name)} />)}
                                        >
                                            <Space direction={"vertical"} style={{ width: "100%" }}>
                                                <Form.Item required label={<Trans>Champ</Trans>} name={[field.name, "field"]}>
                                                    <Select showSearch optionFilterProp={"label"}>
                                                        {formFields.map(i =>
                                                            <Select.Option key={i.value} value={i.value} label={i.label}>{i.label}</Select.Option>
                                                        )}
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item required label={<Trans>Condition</Trans>} name={[field.name, "type"]}
                                                           style={{ width: "100%" }}>
                                                    <Select style={{ width: "100%" }}>
                                                        {CONDITIONS.map(i =>
                                                            <Select.Option key={i} value={i}>{i}</Select.Option>
                                                        )}
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item label={<Trans>Valeurs</Trans>} name={[field.name, "values"]}
                                                           style={{ width: "100%" }}>
                                                    <Select style={{ width: "100%" }} mode={"tags"} />
                                                </Form.Item>
                                            </Space>

                                            <Divider dashed style={{ margin: "0 10px" }}>
                                                <Form.Item name={[field.name, "next"]}>
                                                    <Select placeholder={t`ET`} style={{ width: 75 }}>
                                                        <Select.Option value={"AND"}><Trans>ET</Trans></Select.Option>
                                                        <Select.Option value={"OR"}><Trans>OU</Trans></Select.Option>
                                                    </Select>
                                                </Form.Item>
                                            </Divider>
                                        </Card>
                                    )
                                })}
                                <Button style={{ marginTop: 20 }} type="dashed" onClick={() => add()} block
                                        icon={<PlusOutlined />}>
                                    <Trans>Ajouter une condition</Trans>
                                </Button>
                            </div>
                        )}
                    </Form.List>
                </div>

                <Form.Item name={"action"} label={<Trans>Si Match, alors :</Trans>}>
                    <Select>
                        {CONDITIONS_ACTIONS.map(i => <Select.Option key={i} value={i}>{i}</Select.Option>)}
                    </Select>
                </Form.Item>
            </Form>
        )
    }

    return {
        Renderer: (props) => {
            const { data } = props

            // On utilise absolutePath (chemin ABSOLU dans formValues, INCLUT les
            // productId) — pas `path` (relatif au Form.List, sans productId, fait
            // pour les names Antd). calculCondition navigue dans formValues en
            // remontant les scopes ancêtres de cette branche, sans déborder sur les
            // occurrences sœurs. Ex: [productId, index, "[CONTACT]_Ado"].
            const { absolutePath } = useFormPath()

            const liveRender = (props.isPreviewMode || formPlugin.live)

            const getDisplayFromAction = (action) => {
                switch (action) {
                    case CONDITIONS_ACTIONS[0]:
                        return false
                    case CONDITIONS_ACTIONS[1]:
                        return true
                    default:
                        return true // Si action mal formée, on affiche par défaut (fail-safe)
                }
            }

            const renderAlerts = () => {
                // Volontairement SANS namespace : on veut juste savoir si l'etiquette
                // existe encore quelque part dans le formulaire. Filtrer par namespace
                // ici ferait remonter une fausse alerte sur une condition valide.
                const formFields = formPlugin.getFormFields()
                const conditions = Array.isArray(data.conditions) ? data.conditions : []
                const conditionsFields = conditions.filter(i => i?.field).map(i => i.field)
                const unFindFields = conditionsFields
                    .filter(i => !formFields.includes(i))
                    .filter((i, idx, arr) => arr.indexOf(i) === idx) // une alerte par champ

                return (
                    <Space direction={"vertical"} style={{ margin: "20px 0" }}>
                        {unFindFields.map(i => (
                            <Alert
                                key={i}
                                showIcon
                                type={"warning"}
                                message={t`Le champ "${i}" n'existe pas.`}
                                description={t`Il se peut que vous avez changé l'étiquette du champ, n'oubliez pas de modifier votre condition en conséquence !`}
                            />
                        ))}
                    </Space>
                )
            }

            const finalAction = calculCondition(formPlugin.formValues, data.default, data.conditions, data.action, absolutePath)
            const display = getDisplayFromAction(finalAction)

            if (!display && liveRender) {
                return <div />
            }

            return (
                <div>
                    <div style={{
                        border: liveRender ? "none" : "1px dashed orange",
                        borderRadius: liveRender ? 0 : 10,
                        padding: liveRender ? 0 : "2%"
                    }}>
                        {!liveRender && renderAlerts()}
                        {props.children}
                    </div>
                </div>
            )
        },
        namespace: namespace,
        id: 'form-condition' + namespace,
        title: t`Condition`,
        description: t`Élément de formulaire utilisable.`,
        icon: <NodeExpandOutlined />,
        iconColor: "#2980B9",
        version: 1,
        createInitialData: () => {
            return { default: CONDITIONS_ACTIONS[0], conditions: [], action: CONDITIONS_ACTIONS[1] }
        },
        controls: {
            type: 'custom',
            Component: FormComponent
        },
    }
}
